/* About.jsx — real About page for Kaily Gan.
 *
 * Bilingual (EN + CN) — content is pulled from `/api/site-meta` so Kaily
 * can self-edit the portrait, role, bio, approach and process in the
 * Studio. Image falls back to `meet_kaily_image` so the existing Services
 * portrait upload still works until Kaily uploads a dedicated About shot.
 *
 * No AI portraits, no fabricated clients, no "甘可爱" written back into
 * the page copy.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFab from "@/components/WhatsAppFab";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fieldFor(meta, baseKey, lang) {
    if (!meta) return "";
    const suffix = lang === "cn" ? "_cn" : "_en";
    return (meta[baseKey + suffix] || "").trim();
}

export default function About() {
    const { t, lang } = useT();
    const [meta, setMeta] = useState(null);

    useEffect(() => {
        axios.get(`${API}/site-meta`).then((r) => setMeta(r.data)).catch(() => setMeta({}));
    }, []);

    // SEO head (minimal, matches other pages).
    useEffect(() => {
        const prevTitle = document.title;
        document.title = lang === "cn"
            ? "关于 — Kaily Gan · 餐饮 · 酒店 · 品牌影像创作者"
            : "About — Kaily Gan · F&B, hospitality & brand filmmaker";
        return () => { document.title = prevTitle; };
    }, [lang]);

    const portrait = (meta && (meta.about_image || meta.meet_kaily_image)) || "";
    const crop = (meta && (meta.about_image ? meta.about_crop : meta.meet_kaily_crop)) || { x: 50, y: 50 };
    const role = fieldFor(meta, "about_role", lang) || (lang === "cn" ? "独立餐饮、酒店与品牌影像创作者" : "Independent F&B, hospitality & brand filmmaker");
    const bio = fieldFor(meta, "about_bio", lang) || t("about.intro");
    const approach = fieldFor(meta, "about_approach", lang) || t("about.my_role.body");
    const process = fieldFor(meta, "about_process", lang) || t("about.process.body");

    const workPath = lang === "cn" ? "/work/cn" : "/work";
    const homeContact = lang === "cn" ? "/cn#contact" : "/#contact";

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso" data-testid="about-page">
            <SiteNav />
            <main className="pt-24 md:pt-32 pb-24">
                {/* ---------- Hero ---------- */}
                <section className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                        <div className="lg:col-span-7 order-2 lg:order-1">
                            <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                                {t("about.overline")}
                            </p>
                            <h1
                                className="font-serif text-cafe-espresso leading-[0.95] tracking-[-0.015em] text-balance"
                                data-testid="about-headline"
                                style={{ fontSize: lang === "cn" ? "clamp(2.25rem, 7vw, 5.5rem)" : "clamp(2.5rem, 7.5vw, 6rem)", fontWeight: 500 }}
                            >
                                {t("about.title_1")} <em className="font-display italic text-cafe-olive">{t("about.title_2")}</em>.
                            </h1>
                            <p className="mt-6 text-base md:text-lg text-cafe-espresso/70 leading-relaxed max-w-xl">
                                {bio}
                            </p>
                            <p className="mt-5 text-[11px] uppercase tracking-[0.22em] text-cafe-espresso/60">
                                {role}
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link
                                    to={workPath}
                                    data-testid="about-cta-work"
                                    className="inline-flex items-center h-11 md:h-12 px-6 md:px-7 rounded-full bg-cafe-espresso text-cafe-cream text-[13.5px] font-medium hover:bg-cafe-olive transition-colors"
                                >
                                    {t("about.cta_work")}
                                    <ArrowUpRight className="ml-1.5 h-4 w-4" strokeWidth={1.5} />
                                </Link>
                                <a
                                    href={homeContact}
                                    data-testid="about-cta-contact"
                                    className="inline-flex items-center h-11 md:h-12 px-6 md:px-7 rounded-full border border-cafe-espresso text-cafe-espresso text-[13.5px] font-medium hover:bg-cafe-espresso hover:text-cafe-cream transition-colors"
                                >
                                    {t("about.cta_contact")}
                                </a>
                            </div>
                        </div>
                        <div className="lg:col-span-5 order-1 lg:order-2">
                            <div
                                className="relative w-full bg-cafe-olive overflow-hidden rounded-sm"
                                style={{ aspectRatio: "4 / 5" }}
                                data-testid="about-portrait"
                            >
                                {portrait ? (
                                    <img
                                        src={portrait}
                                        alt={t("about.portrait_alt")}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        style={{ objectPosition: `${crop.x}% ${crop.y}%` }}
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-cafe-cream/70 font-display italic text-sm">
                                        {t("about.portrait_alt")}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ---------- My role ---------- */}
                <section className="mt-20 md:mt-28 border-t border-cafe-espresso/10 pt-16 md:pt-24">
                    <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-5">
                            <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                                {t("about.my_role.overline")}
                            </p>
                            <h2 className="font-serif text-3xl md:text-5xl text-cafe-espresso leading-tight text-balance" style={{ fontWeight: 500 }}>
                                {t("about.my_role.title")}
                            </h2>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="text-base md:text-lg text-cafe-espresso/80 leading-relaxed whitespace-pre-line">
                                {approach}
                            </p>
                        </div>
                    </div>
                </section>

                {/* ---------- Process ---------- */}
                <section className="mt-20 md:mt-28 border-t border-cafe-espresso/10 pt-16 md:pt-24">
                    <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-5">
                            <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                                {t("about.process.overline")}
                            </p>
                            <h2 className="font-serif text-3xl md:text-5xl text-cafe-espresso leading-tight text-balance" style={{ fontWeight: 500 }}>
                                {t("about.process.title")}
                            </h2>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="text-base md:text-lg text-cafe-espresso/80 leading-relaxed whitespace-pre-line">
                                {process}
                            </p>
                            <div className="mt-10 flex flex-wrap gap-3">
                                <a
                                    href={homeContact}
                                    data-testid="about-cta-contact-bottom"
                                    className="inline-flex items-center h-11 md:h-12 px-6 md:px-7 rounded-full bg-cafe-olive text-cafe-cream text-[13.5px] font-medium hover:bg-cafe-oliveDark transition-colors"
                                >
                                    {t("about.cta_contact")}
                                    <ArrowUpRight className="ml-1.5 h-4 w-4" strokeWidth={1.5} />
                                </a>
                                <Link
                                    to={workPath}
                                    className="inline-flex items-center h-11 md:h-12 px-6 md:px-7 rounded-full border border-cafe-espresso text-cafe-espresso text-[13.5px] font-medium hover:bg-cafe-espresso hover:text-cafe-cream transition-colors"
                                >
                                    {t("about.cta_work")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <SiteFooter />
            <WhatsAppFab />
        </div>
    );
}
